import AuthStore from "auth/AuthStore"
import RegionRouterStore from "features/main/RegionRouterStore"
import { makeAutoObservable } from "mobx"
import { MainContainerStore } from "../MainContainerStore"
import OrgGuardStore from "../../OrgGuardStore"
import { Location } from 'react-router-dom'
import RouterStore from "../../RouterStore"
import { Analytics, AnalyticsEvent } from "analytics/analytics"
import { Sentry } from "sentry/sentry"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import variables from "variables/variables"
import { notNull } from "@gap/utils/utils"
import { UserOrgWithOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgWithOrgDto"
import { CacheHandler } from "reusable/utils/cache"
import { apiClient } from "api/trpc/trpc-client"
import { markSessionEndedByUser } from "auth/logout-reason"

interface ProfileMenuStoreProps {
   authStore: AuthStore
   regionRouterStore: RegionRouterStore
   mainContainerStore: MainContainerStore
   orgGuardStore: OrgGuardStore
   location: Location
   routerStore: RouterStore
}

const sortByLabel = (orgA: UserOrgWithOrgDto, orgB: UserOrgWithOrgDto) => orgA.name.localeCompare(orgB.name)

export default class ProfileMenuStore {
   sp
   anchorEl: null | HTMLElement
   anchorElDevMenu: null | HTMLElement

   constructor(sp: ProfileMenuStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get org() {
      return this.sp.orgGuardStore.org
   }

   get open() {
      return Boolean(this.anchorEl)
   }

   get orgs() {
      // Ensure that we return the list in the correct hierarchy, with every org sorted by name
      const allOrgs = (this.sp.orgGuardStore.userData.orgs || []).slice().sort(sortByLabel)
      const gapOrgs = allOrgs.filter(org => org.orgType === 'gap')
      const advisorOrgs = allOrgs.filter(org => org.orgType === 'advisor')
      // If client and not a guest, unless we are a client who is a guest and we are logged in as that guest org
      const clientOrgs = allOrgs.filter(org => org.orgType === 'client' && (org.roleName.toLowerCase() !== 'guest' || org.id === this.sp.orgGuardStore.orgId))

      return [...gapOrgs, ...advisorOrgs, ...clientOrgs]
   }

   get canSeeOrgSettings() {
      return this.sp.orgGuardStore.doesUserHaveFeaturePermission({ featureId: 'my-organisation' })
   }

   get canSeeDevMenu() {
      return notNull(variables.stage).startsWith('dev-')
   }

   handleDevMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      this.anchorElDevMenu = event.currentTarget
   }

   handleDevMenuClose = () => {
      this.anchorEl = null
      this.anchorElDevMenu = null
   }

   logout = async () => {
      markSessionEndedByUser()
      await this.sp.orgGuardStore.loader.executeLoading(async () => {
         await Promise.all([
            apiClient.user.logout.mutate(),
            (async () => {
               await new Promise(resolve => setTimeout(resolve, 100))
               await this.sp.authStore.signOut()
            })()
         ])
         Analytics.reset()
         Sentry.unsetUser()
         CacheHandler.clearAll()
         this.sp.regionRouterStore.navigate('/')
      }, 'Signing out...')
   }

   handleClick = (event: React.MouseEvent<HTMLElement>) => {
      this.anchorEl = event.currentTarget
   }

   handleClose = () => {
      this.anchorEl = null
   }

   switchOrg = async (event: React.MouseEvent<HTMLElement>) => {
      const orgId = event.currentTarget.getAttribute("data-id")
      if (orgId && orgId !== this.org?.id) {
         await this.sp.orgGuardStore.setOrg(orgId, true)
      }
   }

   goToOrgSettings = () => {
      Analytics.emitEvent(AnalyticsEvent.orgSettingsViewed, {
         "Initiated from": "Profile menu",
      })
      this.sp.routerStore.goToNavPath('my-organisation')
   }

   goToMyProfile = () => {
      this.sp.routerStore.goToNavPath('my-profile')
   }
}

export const { Provider: ProfileMenuStoreProvider, useProvidedStore: useProfileMenuStore } = createStoreContext(ProfileMenuStore)
